import type { ClockWidget } from '../types/domain.js'

/**
 * Taille des textes d'un widget Clock.
 *
 * L'opérateur règle une taille **en pourcentage de la hauteur de sa boîte** :
 * agrandir un widget agrandit son texte, ce qui n'était pas le cas avant. Les
 * tailles étaient exprimées en `cqw` de la toile entière — donc en pourcentage
 * de la largeur d'écran — et cinq paliers figés (`SM` à `HERO`) étaient le seul
 * réglage disponible ; redimensionner une boîte ne changeait rien au texte
 * qu'elle contenait.
 *
 * On convertit ici le pourcentage de hauteur en `cqw` de toile, parce que c'est
 * l'unité dans laquelle toute la feuille de style Clock est écrite. Poser un
 * `container-type` sur chaque widget aurait fait basculer d'un coup les
 * paddings, rayons, gouttières et sous-tailles internes sur une autre référence.
 * L'arithmétique est exacte et le reste de la feuille est intact.
 */

/** Toile de référence, en pixels logiques. */
export const CLOCK_CANVAS_WIDTH = 1920

/**
 * Équivalents en `cqw` des anciens paliers, tels qu'ils étaient écrits dans
 * `clock.css`. Ils servent de repli pour les modèles qui n'ont pas encore de
 * taille libre : leur rendu reste identique au pixel près.
 */
const LEGACY_VALUE_CQW: Record<string, number> = {
  SM: 1.45,
  MD: 2,
  LG: 3,
  XL: 4.3,
  HERO: 7.6,
}

/** Anciennes tailles fixes du libellé et du titre, également en `cqw`. */
const LEGACY_LABEL_CQW = 1.05
const LEGACY_TITLE_CQW = 1.8

/** Bornes du réglage libre, en pourcentage de la hauteur de la boîte. */
export const CLOCK_SCALE_MIN_PCT = 2
export const CLOCK_SCALE_MAX_PCT = 100

export interface ClockFontSizes {
  /** Valeur principale — heure, compteur, titre à l'antenne. */
  value: number
  /** Libellé en capitales au-dessus de la valeur. */
  label: number
  /** Titre secondaire sous la valeur. */
  title: number
}

/** Passe d'un pourcentage de la hauteur de boîte à des `cqw` de toile. */
export function heightPctToCanvasCqw(pct: number, widgetHeight: number): number {
  return (pct / 100) * widgetHeight / CLOCK_CANVAS_WIDTH * 100
}

/** Opération inverse : sert à afficher un palier hérité comme un pourcentage. */
export function canvasCqwToHeightPct(cqw: number, widgetHeight: number): number {
  if (widgetHeight <= 0) return 0
  return (cqw / 100) * CLOCK_CANVAS_WIDTH / widgetHeight * 100
}

function clampPct(value: number): number {
  return Math.min(CLOCK_SCALE_MAX_PCT, Math.max(CLOCK_SCALE_MIN_PCT, value))
}

/**
 * Pourcentages de hauteur effectifs d'un widget. Une taille libre absente
 * retombe sur l'équivalent exact de son ancien palier, pour que l'ouverture d'un
 * modèle existant ne déplace rien à l'écran.
 */
export function resolveClockScalePct(widget: Pick<ClockWidget, 'height' | 'style'>): ClockFontSizes {
  const height = Math.max(1, widget.height)
  const style = widget.style as ClockWidget['style'] & {
    valueScale?: number
    labelScale?: number
    titleScale?: number
  }
  const legacyValueCqw = LEGACY_VALUE_CQW[style.size] ?? LEGACY_VALUE_CQW.LG

  return {
    value: clampPct(style.valueScale ?? canvasCqwToHeightPct(legacyValueCqw, height)),
    label: clampPct(style.labelScale ?? canvasCqwToHeightPct(LEGACY_LABEL_CQW, height)),
    title: clampPct(style.titleScale ?? canvasCqwToHeightPct(LEGACY_TITLE_CQW, height)),
  }
}

/**
 * Tailles à poser sur le widget, en `cqw` de toile — l'unité de `clock.css`.
 */
export function resolveClockFontCqw(widget: Pick<ClockWidget, 'height' | 'style'>): ClockFontSizes {
  const pct = resolveClockScalePct(widget)
  const height = Math.max(1, widget.height)
  return {
    value: heightPctToCanvasCqw(pct.value, height),
    label: heightPctToCanvasCqw(pct.label, height),
    title: heightPctToCanvasCqw(pct.title, height),
  }
}
